import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../../../../services/notification.service';
import { TunnelService } from '../../../../../services/tunnel.service';
import { Tunnel, TunnelTriggerMode } from '../../../../../models/tunnel.model';
import {
  TUNNEL_DEFAULT_TRIGGER_MODE,
  TUNNEL_NAME_MAX_LENGTH,
  TUNNEL_TRIGGER_MODE_OPTIONS,
} from '../../../../../constants/tunnel.constants';
import {
  TunnelFormErrors,
  apiErrorMessage,
  buildTunnelPayload,
  hasFormErrors,
  validateTunnelForm,
} from '../../../../../utils/tunnel.util';

/** Create or edit the location's tunnel (`PUT .../tunnel`). */
@Component({
  selector: 'app-tunnel-form-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tunnel-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: [
    '../../lanes/lane-form-modal/lane-form-modal.component.scss',
    './tunnel-form-modal.component.scss',
  ],
})
export class TunnelFormModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() companyId = '';
  @Input() locationId = '';
  /** The tunnel being edited; null creates one. */
  @Input() tunnel: Tunnel | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  readonly triggerModeOptions = TUNNEL_TRIGGER_MODE_OPTIONS;
  readonly nameMaxLength = TUNNEL_NAME_MAX_LENGTH;

  name = '';
  serial = '';
  triggerMode: TunnelTriggerMode = TUNNEL_DEFAULT_TRIGGER_MODE;
  errors: TunnelFormErrors = { name: '', serial: '' };
  isSubmitting = false;

  /**
   * Bumped whenever the modal opens, closes or changes location, so a save that
   * finishes afterwards cannot reload or close the modal of another location.
   */
  private openSeq = 0;
  private readonly tunnelService = inject(TunnelService);
  private readonly messageService = inject(NotificationService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['companyId'] || changes['locationId']) {
      this.openSeq += 1;
    }
    if (changes['isOpen']?.currentValue) {
      this.name = this.tunnel?.name ?? '';
      this.serial = this.tunnel?.serial ?? '';
      this.triggerMode = this.tunnel?.trigger_mode ?? TUNNEL_DEFAULT_TRIGGER_MODE;
      this.errors = { name: '', serial: '' };
      this.isSubmitting = false;
    }
  }

  get modalTitle(): string {
    return this.tunnel ? 'Edit tunnel' : 'Add tunnel';
  }

  get submitLabel(): string {
    return this.tunnel ? 'Save changes' : 'Add tunnel';
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onClose(): void {
    if (!this.isSubmitting) {
      this.close.emit();
    }
  }

  onSubmit(): void {
    if (this.isSubmitting || !this.companyId || !this.locationId) {
      return;
    }
    const values = { name: this.name, serial: this.serial, triggerMode: this.triggerMode };
    this.errors = validateTunnelForm(values);
    if (hasFormErrors(this.errors)) {
      return;
    }
    this.isSubmitting = true;
    const seq = this.openSeq;
    this.tunnelService
      .saveTunnel(this.companyId, this.locationId, buildTunnelPayload(values))
      .subscribe({
        next: response => {
          this.isSubmitting = false;
          if (seq !== this.openSeq) {
            return;
          }
          if (response.status) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.tunnel ? 'Tunnel saved' : 'Tunnel added',
            });
            this.saved.emit();
            this.close.emit();
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to save the tunnel',
          });
        },
        error: err => {
          this.isSubmitting = false;
          if (seq !== this.openSeq) {
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: apiErrorMessage(err, 'Failed to save the tunnel'),
          });
        },
      });
  }
}
